import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PencilIcon, TrashIcon, PlusIcon, SearchIcon } from '@heroicons/react/outline';

const TvaPage = () => {
  // Static TVA data
  const initialTvas = [
    { id: 'TVA-1001', taux: 20.0, description: 'Taux standard', isDefault: true, updatedAt: '2025-03-10T10:30:00' },
    { id: 'TVA-1002', taux: 10.0, description: 'Taux intermédiaire', isDefault: false, updatedAt: '2025-03-09T14:15:00' },
    { id: 'TVA-1003', taux: 5.5, description: 'Taux réduit', isDefault: false, updatedAt: '2025-03-08T09:45:00' },
    { id: 'TVA-1004', taux: 2.1, description: 'Taux super réduit', isDefault: false, updatedAt: '2025-03-07T16:20:00' },
    { id: 'TVA-1005', taux: 0.0, description: 'Exonération de TVA', isDefault: false, updatedAt: '2025-03-06T11:05:00' },
  ];

  const [tvas, setTvas] = useState(initialTvas);
  const [selectedTvas, setSelectedTvas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tvaToDelete, setTvaToDelete] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newTva, setNewTva] = useState({ taux: '', description: '', isDefault: false });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTva, setEditingTva] = useState(null);

  // Filter TVAs based on search term
  const filteredTvas = tvas.filter(tva => 
    tva.taux.toString().includes(searchTerm.toLowerCase()) || 
    tva.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle selection of all TVAs
  const toggleSelectAll = () => {
    if (selectedTvas.length === filteredTvas.length) {
      setSelectedTvas([]);
    } else {
      setSelectedTvas(filteredTvas.map(tva => tva.id));
    }
  };

  // Toggle selection of a single TVA
  const toggleSelect = (tvaId) => {
    if (selectedTvas.includes(tvaId)) {
      setSelectedTvas(selectedTvas.filter(id => id !== tvaId));
    } else {
      setSelectedTvas([...selectedTvas, tvaId]);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (tva = null) => {
    if (tva) {
      setTvaToDelete(tva);
    }
    setIsDeleteModalOpen(true);
  };

  // Handle delete action
  const handleDelete = () => {
    if (tvaToDelete) {
      // Delete single TVA
      setTvas(tvas.filter(t => t.id !== tvaToDelete.id));
      setSelectedTvas(selectedTvas.filter(id => id !== tvaToDelete.id));
    } else {
      // Delete multiple TVAs
      setTvas(tvas.filter(t => !selectedTvas.includes(t.id)));
      setSelectedTvas([]);
    }
    setIsDeleteModalOpen(false);
    setTvaToDelete(null);
  };

  // Open edit modal
  const openEditModal = (tva) => {
    setEditingTva({...tva});
    setIsEditModalOpen(true);
  };

  // Handle edit submission
  const handleEditSubmit = (e) => {
    e.preventDefault();
    setTvas(tvas.map(t => t.id === editingTva.id ? editingTva : t));
    setIsEditModalOpen(false);
    setEditingTva(null);
  };

  // Open add modal
  const openAddModal = () => {
    setNewTva({ taux: '', description: '', isDefault: false });
    setIsAddModalOpen(true);
  };

  // Handle add submission
  const handleAddSubmit = (e) => {
    e.preventDefault();
    
    const newId = `TVA-${1000 + tvas.length + 1}`;
    const createdTva = {
      id: newId,
      taux: parseFloat(newTva.taux),
      description: newTva.description,
      isDefault: newTva.isDefault,
      updatedAt: new Date().toISOString()
    };
    
    // If new TVA is set as default, update other TVAs
    let updatedTvas = [...tvas];
    if (newTva.isDefault) {
      updatedTvas = updatedTvas.map(t => ({
        ...t,
        isDefault: false
      }));
    }
    
    setTvas([...updatedTvas, createdTva]);
    setIsAddModalOpen(false);
    setNewTva({ taux: '', description: '', isDefault: false });
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      {/* Page header */}
      <div className="mb-6 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Taux de TVA</h1>
          <p className="mt-2 text-sm text-gray-700">
            Gérez les différents taux de TVA applicables à vos produits
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={openAddModal}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Ajouter un taux
          </button>
        </div>
      </div>

      {/* Filters and batch actions */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="p-6 flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
          <div className="flex-1 min-w-0">
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
                placeholder="Rechercher par taux ou description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {selectedTvas.length > 0 && (
            <div className="flex items-center">
              <span className="text-sm text-gray-700 mr-3">
                {selectedTvas.length} sélectionné{selectedTvas.length > 1 ? 's' : ''}
              </span>
              <button
                type="button"
                onClick={() => openDeleteModal()}
                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <TrashIcon className="h-4 w-4 mr-1.5" aria-hidden="true" />
                Supprimer
              </button>
            </div>
          )}
        </div>
      </div>

      {/* TVA list */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300 rounded"
                      checked={selectedTvas.length === filteredTvas.length && filteredTvas.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Taux (%)
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dernière modification
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTvas.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg className="h-12 w-12 text-gray-300 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm">Aucun taux de TVA trouvé</p>
                      <button 
                        className="mt-2 text-sm text-[#174540] hover:text-[#0d2926] font-medium"
                        onClick={openAddModal}
                      >
                        Ajouter un taux de TVA
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTvas.map((tva) => (
                  <tr key={tva.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300 rounded"
                          checked={selectedTvas.includes(tva.id)}
                          onChange={() => toggleSelect(tva.id)}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {tva.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {tva.taux.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {tva.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {tva.isDefault ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Par défaut
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Standard
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(tva.updatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => openEditModal(tva)}
                          className="text-[#174540] hover:text-[#0d2926]"
                          title="Modifier"
                        >
                          <PencilIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(tva)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Confirmer la suppression</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {tvaToDelete 
                          ? `Êtes-vous sûr de vouloir supprimer le taux de TVA "${tvaToDelete.description}" (${tvaToDelete.taux}%) ? Cette action est irréversible.`
                          : `Êtes-vous sûr de vouloir supprimer les ${selectedTvas.length} taux de TVA sélectionnés ? Cette action est irréversible.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDelete}
                >
                  Supprimer
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setTvaToDelete(null);
                  }}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add TVA Modal */}
      {isAddModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleAddSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <div className="mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Ajouter un nouveau taux de TVA</h3>
                    <p className="mt-1 text-sm text-gray-500">Remplissez les informations pour créer un nouveau taux de TVA.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-3">
                      <label htmlFor="taux" className="block text-sm font-medium text-gray-700">
                        Taux (%) <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-1">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          name="taux"
                          id="taux"
                          required
                          className="shadow-sm focus:ring-[#174540] focus:border-[#174540] block w-full sm:text-sm border-gray-300 rounded-md"
                          value={newTva.taux}
                          onChange={(e) => setNewTva({...newTva, taux: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-6">
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="description"
                          id="description"
                          required
                          className="shadow-sm focus:ring-[#174540] focus:border-[#174540] block w-full sm:text-sm border-gray-300 rounded-md"
                          value={newTva.description}
                          onChange={(e) => setNewTva({...newTva, description: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-6">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="isDefault"
                            name="isDefault"
                            type="checkbox"
                            className="focus:ring-[#174540] h-4 w-4 text-[#174540] border-gray-300 rounded"
                            checked={newTva.isDefault}
                            onChange={(e) => setNewTva({...newTva, isDefault: e.target.checked})}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="isDefault" className="font-medium text-gray-700">Définir comme taux par défaut</label>
                          <p className="text-gray-500">Ce taux sera utilisé par défaut pour les nouveaux produits.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#174540] text-base font-medium text-white hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Ajouter
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setIsAddModalOpen(false)}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit TVA Modal */}
      {isEditModalOpen && editingTva && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleEditSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <div className="mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Modifier le taux de TVA</h3>
                    <p className="mt-1 text-sm text-gray-500">ID: {editingTva.id}</p>
                  </div>
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-3">
                      <label htmlFor="edit-taux" className="block text-sm font-medium text-gray-700">
                        Taux (%) <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-1">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          name="edit-taux"
                          id="edit-taux"
                          required
                          className="shadow-sm focus:ring-[#174540] focus:border-[#174540] block w-full sm:text-sm border-gray-300 rounded-md"
                          value={editingTva.taux}
                          onChange={(e) => setEditingTva({...editingTva, taux: parseFloat(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-6">
                      <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="edit-description"
                          id="edit-description"
                          required
                          className="shadow-sm focus:ring-[#174540] focus:border-[#174540] block w-full sm:text-sm border-gray-300 rounded-md"
                          value={editingTva.description}
                          onChange={(e) => setEditingTva({...editingTva, description: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-6">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="edit-isDefault"
                            name="edit-isDefault"
                            type="checkbox"
                            className="focus:ring-[#174540] h-4 w-4 text-[#174540] border-gray-300 rounded"
                            checked={editingTva.isDefault}
                            onChange={(e) => setEditingTva({...editingTva, isDefault: e.target.checked})}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="edit-isDefault" className="font-medium text-gray-700">Définir comme taux par défaut</label>
                          <p className="text-gray-500">Ce taux sera utilisé par défaut pour les nouveaux produits.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#174540] text-base font-medium text-white hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Enregistrer
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setEditingTva(null);
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TvaPage;